import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import "../css/ChatHistory.css";
import { debounce } from "lodash";
import Cookies from "js-cookie";

const ChatHistory = () => {
  const { phoneNumber } = useParams();

  // Resolve agent login: from param → cookie → fallback (optional)
  let phone =
    phoneNumber && phoneNumber !== undefined && phoneNumber !== "0"
      ? phoneNumber
      : Cookies.get("phone") || null;

  console.log("Customer phone", phone);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startCount, setStartCount] = useState(0);
  const [endCount, setEndCount] = useState(20);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const chatBoxRef = useRef(null);
  const hasFetchedMessages = useRef(false);

  // Group messages by date and sort by ID
  const groupMessagesByDate = (messages) => {
    // Ensure messages is an array
    if (!Array.isArray(messages)) {
      console.error("Expected messages to be an array but got:", messages);
      return {}; // Return empty object in case of invalid data
    }

    const sortedMessages = [...messages].sort((a, b) => a.id - b.id);

    // Group the messages by date
    return sortedMessages.reduce((groups, message) => {
      if (!message.dateAndTime) {
        console.error("Invalid dateAndTime field:", message);
        return groups; // Skip message if dateAndTime is missing or invalid
      }

      // Parse the date
      const date = new Date(message.dateAndTime).toLocaleDateString();

      // Check if the date was valid
      if (isNaN(new Date(date).getTime())) {
        console.error("Invalid date format:", message.dateAndTime);
        return groups; // Skip message if date format is invalid
      }

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  // Function to get a cookie by name
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const phoneCookie =
        phoneNumber && phoneNumber !== "0"
          ? phoneNumber
          : Cookies.get("phone") || null;

      if (phoneCookie) {
        console.log("Phone from cookie:", phoneCookie);
        phone = phoneCookie;
      } else {
        console.log("Phone cookie not found.");
      }
    }, 2000); // every 10 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  // Save login data
  const saveLogin = async () => {
    setLoading(true);
    const agentIdFromCookie = getCookie("AgentID");

    if (!agentIdFromCookie) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/sms/conversation/${phone}/${agentIdFromCookie}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone,
            login: agentIdFromCookie,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save login data");
      }

      // const data = await response.json();
      // setMessages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateSmsDetails = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/sms/updateSmsDetails?phone=${phone}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save login data");
      }

      // const data = await response.json();
      // setMessages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = (isScrollToTop = false) => {
    if (loading || !hasMoreMessages) return;

    setLoading(true);

    const chatBox = chatBoxRef.current;
    const previousScrollHeight = chatBox ? chatBox.scrollHeight : 0;

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/sms/messages/${phone}?startCount=${startCount}&endCount=${endCount}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch messages");
        }
        return response.json();
      })
      .then((data) => {
        // Ensure 'data' is an array before processing
        if (!Array.isArray(data)) {
          console.error("Expected an array of messages, but got:", data);
          return;
        }

        if (data.length === 0) {
          setHasMoreMessages(false);
        }

        setMessages((prevMessages) => {
          const allMessages = isScrollToTop
            ? [...data, ...prevMessages]
            : [...prevMessages, ...data];

          const uniqueMessages = allMessages.filter(
            (message, index, self) =>
              index === self.findIndex((m) => m.id === message.id)
          );

          return uniqueMessages;
        });
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
        setStartCount(startCount + 20);
        setEndCount(endCount + 20);

        // Restore the scroll position
        setTimeout(() => {
          if (chatBox) {
            chatBox.scrollTop =
              chatBox.scrollTop + (chatBox.scrollHeight - previousScrollHeight);
          }
        }, 0); // Adjust timing if necessary
      });
  };

  // Handle scroll event
  const handleScroll = useCallback(
    debounce(() => {
      if (chatBoxRef.current?.scrollTop === 0 && hasMoreMessages && !loading) {
        fetchMessages(true);
      }
    }, 300),
    [hasMoreMessages, loading]
  );

  // Attach and detach scroll event
  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (chatBox) {
      chatBox.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatBox) {
        chatBox.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  // Initial fetch on mount
  useEffect(() => {
    if (!hasFetchedMessages.current && phone && phone.length >= 10) {
      fetchMessages();
      saveLogin();
      updateSmsDetails();
      hasFetchedMessages.current = true;
      scrollToBottom(); // Scroll to bottom on first render
    }
  }, []);

  const scrollToBottomConditionally = () => {
    if (chatBoxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // Adjust threshold if needed
      if (isAtBottom) {
        scrollToBottom();
      }
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottomConditionally();
    }
  }, [messages]);

  // Scroll to bottom on new messages
  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className="chat-container">
      <div className="chat-history-header">
        <h2>Chat History for {phone}</h2>
      </div>
      <div className="chat-box" ref={chatBoxRef}>
        {Object.entries(groupedMessages).map(([date, messagesForDate]) => (
          <div key={date} className="date-group">
            <div className="date-divider">{date}</div>
            <div className="message-column">
              {messagesForDate.map((message) => (
                <div
                  key={message.id}
                  className={`chat-message ${
                    message.sender === "Agent"
                      ? "customer-message"
                      : "agent-message"
                  }`}
                >
                  {message.message}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* {hasMoreMessages && (
                <div className="load-more-container">
                    {loading ? (
                        <button disabled>Loading...</button>
                    ) : (
                        <button onClick={fetchMessages}>Load More</button>
                    )}
                </div>
            )} */}

      {!hasMoreMessages && messages.length > 0 && (
        <div className="no-more-messages">No more messages to load</div>
      )}

      {loading && messages.length === 0 && <div>Loading...</div>}
      {error && <div>Error: {error}</div>}
    </div>
  );
};

export default ChatHistory;
